// Type-safe wrapper for Google Analytics
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Onboarding funnel event names for tracking user journey
 */
export type OnboardingEvent =
  | 'signup_complete'
  | 'email_verified'
  | 'mobile_verified'
  | 'shipping_address_added'
  | 'first_trade_outbound'
  | 'first_trade_inbound';

/**
 * Get referral source from localStorage (stored from URL params on landing)
 * Supports both ?ref=partner and ?utm_source=partner URLs
 */
const getReferralSource = (): string | undefined => {
  try {
    return localStorage.getItem('referral_source') || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Get standard user properties for GA4 events
 * @param user - Object containing user id, createdAt, and country
 * @returns Object with user_id, signup_date, country, and referral_source
 */
export const getUserProperties = (user: {
  id?: number;
  createdAt?: string;
  country?: string | null;
}) => ({
  user_id: user.id?.toString(),
  signup_date: user.createdAt ? user.createdAt.split('T')[0] : undefined,
  country: user.country || undefined,
  referral_source: getReferralSource(),
});

/**
 * Track a page view in Google Analytics
 * @param url - The full URL path including search params (e.g., "/my-cards?tab=want-list")
 */
export const trackPageView = (url: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-NKKX593FTB', {
      page_path: url,
    });
  }
};

/**
 * Track a custom event in Google Analytics
 * @param eventName - Name of the event (e.g., "trade_completed", "card_added")
 * @param eventParams - Optional parameters for the event
 *
 * @example
 * trackEvent('trade_completed', {
 *   trade_value: 50.00,
 *   card_count: 5
 * });
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};
